import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { useTranslation } from 'react-i18next';

const BOOKING_FORM_URL = 'https://rgradcpiwm98zpetuigzzghomf3fmzoq.forms.pienissimo.pro/?id=Fl7E3J';

type Course = { section: string; items: string[] };
type HolidayMenu = {
  id: string;
  date: string;
  name: string;
  meal: string;
  courses: Course[];
  included: string[];
};

const MenuFestivi = () => {
  const { t } = useTranslation(['menuFestivi', 'seo']);
  const menus = t('menuFestivi:menus', { returnObjects: true }) as HolidayMenu[];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name: t('menuFestivi:title'),
    hasMenuSection: menus.map((menu) => ({
      '@type': 'MenuSection',
      name: `${menu.name} - ${menu.date}`,
      hasMenuItem: menu.courses.flatMap((course) =>
        course.items.map((item) => ({ '@type': 'MenuItem', name: item })),
      ),
    })),
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t('seo:menuFestivi.title')}
        description={t('seo:menuFestivi.description')}
        keywords={t('seo:menuFestivi.keywords')}
        logicalPath="/menu-festivi"
        structuredData={structuredData}
      />
      <Header />

      {/* Intro */}
      <section className="pt-32 pb-12 px-6">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <span className="text-sm font-light uppercase tracking-[0.3em] text-primary">
              {t('menuFestivi:eyebrow')}
            </span>
            <h1 className="mt-4 mb-6 text-5xl font-light italic text-foreground md:text-6xl">
              {t('menuFestivi:title')}
            </h1>
            <p className="mb-10 text-lg font-light text-muted-foreground">{t('menuFestivi:intro')}</p>
          </ScrollReveal>

          <nav aria-label={t('menuFestivi:jumpNavAria')} className="flex flex-wrap justify-center gap-3">
            {menus.map((menu) => (
              <a
                key={menu.id}
                href={`#${menu.id}`}
                className="rounded-full border border-border px-4 py-2 text-xs font-light uppercase tracking-wider text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
              >
                {menu.date}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Menus */}
      <section className="pb-16 px-6">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2">
          {menus.map((menu, index) => (
            <ScrollReveal
              key={menu.id}
              delay={(index % 2) * 100}
              className={index === menus.length - 1 && menus.length % 2 === 1 ? 'md:col-span-2 md:mx-auto md:w-1/2' : ''}
            >
              <article
                id={menu.id}
                className="h-full scroll-mt-28 rounded-lg border border-border bg-card px-6 py-10 text-center md:px-10"
              >
                <p className="text-xs font-light uppercase tracking-[0.3em] text-primary">{menu.date}</p>
                <h2 className="mt-3 text-3xl font-light uppercase tracking-[0.1em] text-foreground md:text-4xl">
                  {menu.name}
                </h2>
                <p className="mt-2 font-light italic text-muted-foreground">{menu.meal}</p>

                <div className="mx-auto my-8 h-px w-16 bg-border" />

                <div className="space-y-8">
                  {menu.courses.map((course) => (
                    <div key={course.section}>
                      <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-foreground">
                        {t(`menuFestivi:sections.${course.section}`)}
                      </h3>
                      <ul className="space-y-2">
                        {course.items.map((item) => (
                          <li key={item} className="text-sm font-light text-muted-foreground md:text-base">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="mx-auto my-8 h-px w-16 bg-border" />

                {menu.included.map((line) => (
                  <p key={line} className="text-xs font-light uppercase tracking-wide text-muted-foreground">
                    {line}
                  </p>
                ))}
              </article>
            </ScrollReveal>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Button variant="hero" size="lg" asChild>
            <a href="#prenota-festivita">{t('menuFestivi:booking.cta')}</a>
          </Button>
        </div>
      </section>

      {/* Booking form */}
      <section id="prenota-festivita" className="scroll-mt-24 pb-24 px-6">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="mb-8 text-center">
              <h2 className="mb-4 text-3xl font-semibold text-foreground md:text-4xl">
                {t('menuFestivi:booking.title')}
              </h2>
              <p className="text-muted-foreground">{t('menuFestivi:booking.subtitle')}</p>
            </div>
          </ScrollReveal>

          <div className="relative w-full overflow-hidden rounded-lg border border-border bg-card">
            <iframe
              src={BOOKING_FORM_URL}
              className="h-[1500px] w-full border-0"
              title={t('menuFestivi:booking.iframeTitle')}
              allow="clipboard-write"
              loading="lazy"
            />
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default MenuFestivi;
